import { CommonModule } from '@angular/common';
import {
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Component } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { UserSessionModel } from '../../../core/models/user/current-user.model';
import { AuthService } from '../../../core/services/user/auth.service';
import { CustomersService } from '../../../core/services/customer/customers.service';
import { CurrentUserService } from '../../../core/services/user/current-user.service';
import { CookieService } from '../../../core/services/cookie.service';
import { passwordMatchValidator } from '../../validators/password-match-validator';
import { GoogleQrCodeDetailsModel } from '../../../core/models/user/google-qr-code-details.model';
import { LanguagesListModel } from '../../../core/models/user/languages-model';
import { AuthDetailsModel } from '../../../core/models/user/auth-details.model';
import { TwoFactorAuthStatus } from '../../../../enums/two-factor-auth.enum';
import { ToastService } from '../../../core/services/toast.service';
import { WelcomeImageComponent } from '../../components/welcome-image/welcome-image.component';

declare var KTMenu: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    WelcomeImageComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  firstLoginForm: FormGroup;
  mailCodeForm: FormGroup;
  mailLoginForm: FormGroup;
  googleLoginForm: FormGroup;
  googleQrCodeForm: FormGroup;
  currentUser: UserSessionModel | null = null;
  secretQuestions: any = [];
  langChangeSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private cookieService: CookieService,
    private toastService: ToastService
  ) {
    const userLang = 'tr';
    this.translate.use(userLang);

    this.firstLoginForm = this.fb.group(
      {
        secretQuestion: [null, [Validators.required]],
        answer: ['', [Validators.required]],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[A-Z])(?=.*[!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|])[A-Za-z\d!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|]{8,}$/
            ),
          ],
        ],
        confirmPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[A-Z])(?=.*[!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|])[A-Za-z\d!@#$%^&*()_\-+=\[{\]};:'",<.>/?\\|]{8,}$/
            ),
          ],
        ],
      },
      { validators: passwordMatchValidator }
    );
    this.mailCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.googleLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.googleQrCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
  }
  loginForm = {
    email: '',
    password: '',
  };
  languages: LanguagesListModel[] = [
    {
      code: 'tr',
      name: 'Türkçe',
      flag: 'assets/media/flags/turkey.png',
    },
  ];
  currentYear: number = new Date().getFullYear();
  loginLoading: boolean = false;
  firstLoginLoading: boolean = false;
  mailAuthSectionLoading: boolean = false;
  loginWithMailLoading: boolean = false;
  loginWithGoogleLoading: boolean = false;
  showFirstLogin: boolean = false;
  show2FA: boolean = false;
  showMailSection: boolean = false;
  showLoginWithMail: boolean = false;
  showLoginWithGoogle: boolean = false;
  googleQrCodeDetails: GoogleQrCodeDetailsModel = {
    qrCodeImageUrl: '',
    manuelEntrySetupCode: '',
  };
  loginPasswordVisible: boolean = false;
  currentLanguageName: string = '';
  currentLanguageFlag: string = '';

  ngOnInit() {
    const langCode = localStorage.getItem('selectedLanguage');
    if (!langCode) {
      localStorage.setItem('selectedLanguage', 'tr');
    }
    const selectedLang =
      this.languages.find((lang) => lang.code === langCode) ||
      this.languages[0];
    this.updateCurrentLanguage(selectedLang);
    this.loadTranslations();
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
    this.secretQuestions = [
      {
        id: 1,
        secretQuestion: this.translate.instant(
          'secretQuestions.favouriteRestaurant'
        ),
      },
      {
        id: 2,
        secretQuestion: this.translate.instant('secretQuestions.firstPet'),
      },
      {
        id: 3,
        secretQuestion: this.translate.instant(
          'secretQuestions.historicalPerson'
        ),
      },
      {
        id: 4,
        secretQuestion: this.translate.instant('secretQuestions.firstBoss'),
      },
      {
        id: 5,
        secretQuestion: this.translate.instant(
          'secretQuestions.favouriteAuthor'
        ),
      },
    ];
  }

  ngAfterViewInit(): void {
    if (KTMenu) {
      KTMenu.createInstances();
    }
  }

  loadTranslations(): void {
    this.translate
      .get([
        'secretQuestions.favouriteRestaurant',
        'secretQuestions.firstPet',
        'secretQuestions.historicalPerson',
        'secretQuestions.firstBoss',
        'secretQuestions.favouriteAuthor',
      ])
      .subscribe((translations) => {
        this.secretQuestions = [
          {
            id: 1,
            secretQuestion: translations['secretQuestions.favouriteRestaurant'],
          },
          { id: 2, secretQuestion: translations['secretQuestions.firstPet'] },
          {
            id: 3,
            secretQuestion: translations['secretQuestions.historicalPerson'],
          },
          { id: 4, secretQuestion: translations['secretQuestions.firstBoss'] },
          {
            id: 5,
            secretQuestion: translations['secretQuestions.favouriteAuthor'],
          },
        ];
      });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  setLanguage(lang: { code: string; name: string; flag: string }) {
    this.translate.use(lang.code);
    localStorage.setItem('selectedLanguage', lang.code);
    this.currentLanguageName = lang.name;
    this.currentLanguageFlag = lang.flag;
  }

  private updateCurrentLanguage(lang: {
    code: string;
    name: string;
    flag: string;
  }) {
    this.currentLanguageName = lang?.name;
    this.currentLanguageFlag = lang?.flag;
  }

  isFirstLoginControlInvalid(controlName: string): boolean {
    const control = this.firstLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isMailLoginControlInvalid(controlName: string): boolean {
    const control = this.mailLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  isGoogleLoginControlInvalid(controlName: string): boolean {
    const control = this.googleLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onSecretQuestionChange(e: Event) {
    const selectedSecretQuestion = (e.target as HTMLSelectElement).value;
    this.firstLoginForm.get('secretQuestion')?.setValue(selectedSecretQuestion);
  }

  async submitLogin(e: Event): Promise<void> {
    e.preventDefault();
    this.loginLoading = true;
    const selectedLanguageCode =
      localStorage.getItem('selectedLanguage') || 'tr';
    localStorage.setItem('isLogin', 'false');

    const res: AuthDetailsModel = await this.authService
      .login(
        this.loginForm.email,
        this.loginForm.password,
        selectedLanguageCode
      )
      .pipe(finalize(() => (this.loginLoading = false)))
      .toPromise();
    if (res?.status === 1) {
      this.showFirstLogin = true;
    } else {
      this.currentUser = this.currentUserService.GetSession();
      if (res?.authenticatorType === TwoFactorAuthStatus.None) {
        this.router.navigate(['/dashboard']);
      } else if (res?.authenticatorType === TwoFactorAuthStatus.Email) {
        this.showLoginWithMail = true;
      } else if (res?.authenticatorType === TwoFactorAuthStatus.Google) {
        this.showLoginWithGoogle = true;
      }
    }
  }

  submitFirstLogin(e: Event): void {
    e.preventDefault();
    if (this.firstLoginForm.valid) {
      this.firstLoginLoading = true;
      const formData = {
        email: this.loginForm.email,
        oldPassword: this.loginForm.password,
        ...this.firstLoginForm.value,
      };

      this.authService
        .firstLogin(formData)
        .pipe(
          finalize(() => {
            this.firstLoginLoading = false;
          })
        )
        .subscribe({
          next: () => {
            this.show2FA = true;
            this.toastService.showToast(
              this.translate.instant('alert.processCompletedSuccessfully'),
              'success'
            );
          },
        });
    } else {
      this.firstLoginForm.markAllAsTouched();
      this.toastService.showToast(
        this.translate.instant('warningRequiredFieldsDesc'),
        'warning'
      );
    }
  }

  async getApplicationsByCustomerCode(customerCode: string): Promise<boolean> {
    const res = await this.customersService
      .getApplicationsByCustomerCode(customerCode ?? '')
      .toPromise();
    this.cookieService.setCookie('applications', JSON.stringify(res), 1);
    const isLogin = localStorage.getItem('isLogin');
    if (res.length === 1 && isLogin === 'false') {
      localStorage.setItem('isLogin', 'true');
    }
    return false;
  }

  onGoogleAuthToggle(): void {
    this.authService.getGoogleAuth().subscribe({
      next: async (res: GoogleQrCodeDetailsModel) => {
        this.googleQrCodeDetails.qrCodeImageUrl = res?.qrCodeImageUrl;
        this.googleQrCodeDetails.manuelEntrySetupCode =
          res?.manuelEntrySetupCode;
      },
    });
  }

  onMailAuthToggle(e: Event): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    if (isChecked) {
      this.showMailSection = true;
      this.authService.sendEmailAuthForEnable().subscribe({
        next: () => {
          this.showMailSection = true;
        },
      });
    }
  }

  // Login with Mail
  async submitMailLoginForm(e: Event): Promise<void> {
    e.preventDefault();
    if (this.mailLoginForm.valid) {
      this.loginWithMailLoading = true;
      const formData = {
        email: this.loginForm.email,
        password: this.loginForm.password,
        ...this.mailLoginForm.value,
      };

      this.authService
        .loginWithMailAuthenticator(formData)
        .pipe(
          finalize(() => {
            this.loginWithMailLoading = false;
          })
        )
        .subscribe({
          next: async (res) => {
            this.toastService.showToast(
              this.translate.instant('alert.processCompletedSuccessfully'),
              'success'
            );

            const existSingleApp = await this.getApplicationsByCustomerCode(
              res?.customerCode
            );
            if (!existSingleApp) {
              this.router.navigate(['/dashboard']);
            }
          },
        });
    } else {
      this.toastService.showToast(
        this.translate.instant('Code required'),
        'warning'
      );
      this.mailLoginForm.markAllAsTouched();
    }
  }

  // Login with Google
  submitGoogleLoginForm(e: Event): void {
    e.preventDefault();
    if (this.googleLoginForm.valid) {
      this.loginWithGoogleLoading = true;
      const formData = {
        email: this.loginForm.email,
        password: this.loginForm.password,
        ...this.googleLoginForm.value,
      };

      this.authService
        .loginWithGoogleAuthenticator(formData)
        .pipe(
          finalize(() => {
            this.loginWithGoogleLoading = false;
          })
        )
        .subscribe({
          next: async (res) => {
            this.toastService.showToast(
              this.translate.instant('alert.processCompletedSuccessfully'),
              'success'
            );

            const existSingleApp = await this.getApplicationsByCustomerCode(
              res?.customerCode
            );
            if (!existSingleApp) {
              this.router.navigate(['/dashboard']);
            }
          },
        });
    } else {
      this.toastService.showToast(
        this.translate.instant('Code required'),
        'warning'
      );

      this.googleLoginForm.markAllAsTouched();
    }
  }

  submitMailAuthCode(e: Event): void {
    e.preventDefault();
    if (this.mailCodeForm.valid) {
      this.mailAuthSectionLoading = true;
      const formData = {
        ...this.mailCodeForm.value,
      };

      this.authService
        .enableEmailAuth(formData)
        .pipe(
          finalize(() => {
            this.mailAuthSectionLoading = false;
          })
        )
        .subscribe({
          next: () => {
            this.showFirstLogin = false;
            this.show2FA = false;
            this.showLoginWithMail = false;
            this.showLoginWithGoogle = false;
            this.showMailSection = false;

            this.toastService.showToast(
              this.translate.instant('alert.processCompletedSuccessfully'),
              'success'
            );

            setTimeout(() => {
              this.router.navigate(['/dashboard']);
              this.signOut();
            }, 2000);
          },
        });
    } else {
      this.toastService.showToast(
        this.translate.instant('Code required'),
        'warning'
      );

      this.mailCodeForm.markAllAsTouched();
    }
  }

  submitGoogleQrCodeForm(e: Event): void {
    e.preventDefault();
    setTimeout(() => {
      if (this.googleQrCodeForm.valid) {
        const formData = {
          ...this.googleQrCodeForm.value,
        };

        this.authService.enableGoogleAuth(formData).subscribe({
          next: () => {
            this.showFirstLogin = false;
            this.show2FA = false;
            this.showLoginWithMail = false;
            this.showLoginWithGoogle = false;

            this.toastService.showToast(
              this.translate.instant('alert.processCompletedSuccessfully'),
              'success'
            );

            setTimeout(() => {
              this.signOut();
            }, 2000);
          },
        });
      } else {
        this.toastService.showToast(
          this.translate.instant('Code required'),
          'warning'
        );

        this.googleQrCodeForm.markAllAsTouched();
      }
    }, 500);
  }

  togglePasswordVisibility(type: string): void {
    if (type === 'login')
      this.loginPasswordVisible = !this.loginPasswordVisible;
  }

  signOut() {
    this.authService.logout().subscribe(() => {
      this.cookieService.deleteCookie('applications');
    });
  }
}
